import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Divider,
  Button,
  Box,
  Drawer,
  IconButton
} from '@mui/material';
import {
  Flag as FlagIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import InlineEditField from './InlineEditField';

export interface PromptManagementFormValues {
  name: string;
  prompt: string;
  listening: string;
  rapport: string;
  valueArticulation: string;
  discovery: string;
  nextSteps: string;
}
export type PromptFormErrors = Partial<Record<keyof PromptManagementFormValues, string>>;
export type PromptManagementTabMode = 'create' | 'edit' | 'view';

interface AdminPromptManagementTabProps {
  values: PromptManagementFormValues;
  onChange: (values: PromptManagementFormValues) => void;
  mode: PromptManagementTabMode;
  editable: boolean;
  errors?: PromptFormErrors;
  saving?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
}

// Explanations for Drawer
const sectionFieldExplanations = {
  roleplay: {
    rubric: `
      You are an elite sales coach, renowned for your high standards. You will critically evaluate call transcripts from meetings between a salesperson at SHI (an IT Solutions Provider) and a customer. Your feedback should be honest and highlight any shortcomings in their performance. Note that this transcript is AI generated and may include grammatical errors or spelling mistakes, which you should ignore for the purpose of evaluation.

      Please provide a precise score from 1 to 5 (to the tenth place) for each of the five evaluation categories, guided by the corresponding questions. Apply the highest grading standards and do not hesitate to deliver severe critiques where warranted.

      The scoring should adhere strictly to the following grading scale:

      1-2: Major Improvements Required – The performance is significantly below par and must be improved immediately.

      2-3: Improvement Needed – There are noticeable gaps that need addressing.

      3-4: Meets Expectations – Acceptable, but not exceptional performance.

      4.0-5: Exceeds Expectations – Truly outstanding performance that stands out.

      When delivering your feedback, avoid using gender-specific pronouns (he/she/him/her) and instead use gender-neutral language (the/they/them/their).
    `,
    listening: `
      Did the seller demonstrate genuine curiosity and ask thoughtful, open-ended questions?

      Did they allow the customer space to respond and pursue relevant follow-ups?

      Did they seek to understand the customer’s role, responsibilities, and key priorities?

      Did they uncover pain points, challenges, or recent/compelling events? 

      Did they avoid jumping prematurely into a pitch before fully understanding the customer’s situation?

      Did the seller dominate the conversation (spoke more than 55% of the time)?
    `,
    rapport: `
      Did the seller establish an early connection and show respect for the customer’s context or mission?

      Did they adapt their language and style to match the customer’s communication preferences?

      Did they show empathy for the customer’s challenges?
    `,
    valueArticulation: `
      Did the seller demonstrate knowledge of SHI’s offerings and capabilities? 

      Did they explain these offerings in terms relevant to the customer’s needs?

      Did they connect SHI’s value to the customer’s specific challenges or goals?

      Did they avoid pitching value-adds before gathering sufficient context?

      Did they reference real-world examples of SHI supporting similar customers? 

      Did they articulate the general benefits and outcomes of the solution or approach being discussed? 
    `,
    discovery: `
      Did they ask about the customer’s main challenges or ongoing initiatives?

      Did they inquire about any requirements, standards, or organizational policies that may impact the customer’s needs or decisions? 

      Did they explore how the customer is addressing ongoing changes or challenges in their environment?

      Did they ask about current processes, tools, vendors, and decision-makers?

      Did they gather enough technical and process information to prepare SHI’s [engineering/technical/resource] for a follow-up?

      Did they ask about any constraints, resources, or opportunities that may impact the customer’s ability to move forward?

      Did they determine the impact or urgency of the challenge or objective?
    `,
    nextSteps: `
      Did the seller recap key discussion points and organize next steps clearly?

      Did they propose relevant next actions (e.g., workshop, technical review, demonstration, exploration of solutions)?

      Did they set timelines and assign responsibility for follow-up?
      
      Did they secure a commitment for a next meeting?
    `,
  }
} as const;

type SectionKey = keyof typeof sectionFieldExplanations;
type RoleplayPromptFieldKey = keyof typeof sectionFieldExplanations['roleplay'];
type SectionFieldTuple = [SectionKey, RoleplayPromptFieldKey];

function camelToTitle(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

const AdminPromptManagementTab: React.FC<AdminPromptManagementTabProps> = ({
  values,
  onChange,
  mode,
  editable,
  errors = {},
  saving,
  onSave,
  onCancel,
  onEdit,
}) => {
  const [sidecarSection, setSidecarSection] = useState<SectionKey | null>(null);
  const [sidecarSectionField, setSidecarSectionField] = useState<SectionFieldTuple | null>(null);

  const handleFieldChange = (field: keyof PromptManagementFormValues, value: string) => {
    onChange({ ...values, [field]: value });
  };

  // Helper for InlineEditField info clicks
  const handleInfoClick = (field: RoleplayPromptFieldKey) => {
    setSidecarSection('roleplay');
    setSidecarSectionField(['roleplay', field]);
  };

  return (
    <Stack spacing={4}> 
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <FlagIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Rubric Builder
        </Typography>
      </Stack>
      <Divider sx={{ mb: 3 }} />

      {/* <Paper elevation={2} sx={{ p: 3, overflow: 'auto', height: '55vh' }}> */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          <InlineEditField
            label="Name"
            value={values.name}
            onSave={val => handleFieldChange('name', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.name}
            helperText={errors.name}
          />
          <InlineEditField
            label="Grading Context / Persona"
            value={values.prompt}
            onSave={val => handleFieldChange('prompt', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.prompt}
            helperText={errors.prompt}
            info='prompt'
            onInfoClick={() => handleInfoClick('rubric')}
          />
          <InlineEditField
            label="Active Listening & Curiosity"
            value={values.listening}
            onSave={val => handleFieldChange('listening', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.listening}
            helperText={errors.listening}
            info='listening'
            onInfoClick={() => handleInfoClick('listening')}
          />
          <InlineEditField
            label="Rapport Building"
            value={values.rapport}
            onSave={val => handleFieldChange('rapport', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.rapport}
            helperText={errors.rapport}
            info='rapport'
            onInfoClick={() => handleInfoClick('rapport')}
          />
          <InlineEditField
            label="Value Articulation & Customer Needs"
            value={values.valueArticulation}
            onSave={val => handleFieldChange('valueArticulation', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.valueArticulation}
            helperText={errors.valueArticulation}
            info='valueArticulation'
            onInfoClick={() => handleInfoClick('valueArticulation')}
          />
          <InlineEditField
            label="Customer Discovery"
            value={values.discovery}
            onSave={val => handleFieldChange('discovery', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.discovery}
            helperText={errors.discovery}
            info='discovery'
            onInfoClick={() => handleInfoClick('discovery')}
          />
          <InlineEditField
            label="Clear & Actionable Next Steps"
            value={values.nextSteps}
            onSave={val => handleFieldChange('nextSteps', val)}
            multiline
            alwaysEditing={editable}
            error={!!errors.nextSteps}
            helperText={errors.nextSteps}
            info='nextSteps'
            onInfoClick={() => handleInfoClick('nextSteps')}
            boldSubstrings={["duration"]}
          />
        </Stack>
        
        <Drawer
          anchor="right"
          open={!!sidecarSectionField}
          onClose={() => {
            setSidecarSection(null);
            setSidecarSectionField(null);
          }}
        >
          <Box sx={{ width: 350, p: 2 }}>
            <IconButton
              sx={{ float: 'right' }}
              onClick={() => {
                setSidecarSection(null);
                setSidecarSectionField(null);
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" gutterBottom>
              {sidecarSectionField ? camelToTitle(sidecarSectionField[1]) : ''}
            </Typography>
            {sidecarSectionField && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  {sectionFieldExplanations[sidecarSectionField[0]][sidecarSectionField[1]]
                    .split('\n')
                    .map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </Typography>            
              </Box>
            )}
          </Box>
        </Drawer>
      {/* </Paper> */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {mode === 'edit' && (
          <>
            {/* <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              Save
            </Button> */}
            {onCancel && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
          </>
        )}
        {mode === 'create' && (
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            Create Assessment
          </Button>
        )}
        {mode === 'view' && onEdit && (
          <Button variant="contained" onClick={onEdit} startIcon={<EditIcon />}>
            Edit
          </Button>
        )}
      </Box>
      {/* <Box sx={{paddingBottom: '20%'}}></Box> */}
    </Stack>
  );
};

export default AdminPromptManagementTab;